/**
 * Floor-plan wire headers, declared once (#1291).
 *
 * ## Why this file exists
 *
 * `EDITOR_EPOCH_HEADER` used to be written out twice — once in
 * `apps/api/src/services/floorPlanEpoch.ts`, once in `apps/web/src/lib/api.ts`
 * — held together by a comment saying they must match. That is how
 * `WRITE_ID_HEADER` and `IDEMPOTENCY_HEADER` are still done, and for an
 * ordinary header it is a fair trade: a wire constant is cheap to mirror and
 * the shared package's export map is not free.
 *
 * This one is not ordinary, because **the #893 fence deliberately fails open**.
 * `assertEditorEpoch` returns silently when the header is absent — a caller
 * with no editor session (the SH3D import, the portable-document import, an
 * API client, a test fixture) has no epoch to quote and must not be refused.
 *
 * So a rename of either copy did not break anything loudly. It unfenced *every*
 * editor write: no 409, no error, and the server reading "no editor session"
 * off a client that believed it was fencing. Measured before this file existed,
 * on `cbd8d05`: renaming the web copy alone left **1792/1792 web unit tests
 * green**, and renaming the API copy alone left **31/31 epoch integration tests
 * green** — the integration suite imports the constant, so it renamed the send
 * and the read together and went on proving a fence that was no longer there.
 * Only the floor-plan e2e would have caught it, and that does not run on PRs.
 *
 * A safety mechanism whose disablement is invisible to the entire test suite is
 * worth one shared string.
 *
 * ## Rules for this file
 *
 * Strings only, no imports, no zod. The web bundle imports it directly (via the
 * `@household/shared/floor-plan-headers` subpath, never the root barrel), so
 * anything that lands here ships to the browser.
 */
/**
 * The plan generation the Home Map editor captured, sent with every write it
 * makes against a plan's graph (#893).
 *
 * Lower-case because Node lower-cases inbound header names before they reach
 * `request.headers`, so a comparison against this constant has to match that
 * form.
 */
export const EDITOR_EPOCH_HEADER = "x-floor-plan-editor-epoch";
